/**
 * Google Places API Integration
 * Handles autocomplete predictions and place details for address validation
 */
import { smartFetch } from '../foundation/smartfetch';
import { buildUrl } from '../foundation/urlbuilder';
const debug = false;
/**
 * GooglePlacesService — Thin service for Google Places API interactions using googleapis
 */
export class GooglePlacesService {
    apiKey = null;
    sessionToken = null;
    requestCache = new Map();
    cacheTTL = 3600000; // 1 hour default
    constructor(config) {
        if (config) {
            this.apiKey = config.apiKey || config.integrations?.googlePlaces?.apiKey || config.integrations?.google?.api_key || null;
            this.cacheTTL = config.cacheTTL || config.integrations?.googlePlaces?.cacheTTL || 3600000;
        }
        this.sessionToken = this.generateSessionToken();
        if (debug) {
            console.log('[GooglePlacesService] constructor', {
                apiKeyConfigured: Boolean(this.apiKey),
                cacheTTL: this.cacheTTL,
                sessionToken: this.sessionToken,
            });
        }
    }
    /**
     * Generate or return cached session token for Places requests
     */
    generateSessionToken() {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    /**
     * Get autocomplete predictions for a search input
     * Uses Google Places Autocomplete API (web service version via googleapis)
     */
    async getPlacePredictions(input, config) {
        if (!input || input.length < 2)
            return [];
        const cacheKey = `predictions_${input}`;
        const cached = this.requestCache.get(cacheKey);
        if (cached)
            return cached;
        try {
            const apiKey = config?.integrations?.googlePlaces?.apiKey || config?.apiKey || config?.integrations?.google?.api_key || this.apiKey;
            if (!apiKey) {
                console.error('Google Places API key not configured');
                return [];
            }
            const restrictions = config?.integrations?.googlePlaces?.countryRestrictions || ['us'];
            const params = {
                input: input,
                key: apiKey,
                sessiontoken: this.sessionToken,
            };
            if (restrictions.length > 0) {
                params.components = `country:${restrictions.join('|country:')}`;
            }
            // Use global proxy to avoid CORS issues
            const proxyURL = config?.integrations?.global?.proxyUrl || '';
            if (debug) {
                console.log('[GooglePlacesService] getPlacePredictions start', {
                    input,
                    restrictions,
                    proxyURL,
                });
            }
            const apiUrl = buildUrl({
                baseUrl: 'https://maps.googleapis.com',
                pathSegments: ['maps', 'api', 'place', 'autocomplete', 'json'],
                params: params,
            });
            const proxyOptions = proxyURL ? { url: proxyURL, forceProxy: true, fallbackOnCors: true } : undefined;
            if (debug) {
                console.log('[GooglePlacesService] getPlacePredictions request', {
                    apiUrl,
                    proxyOptions,
                });
            }
            const data = await smartFetch(apiUrl, {
                proxy: proxyOptions,
            });
            if (debug) {
                console.log('[GooglePlacesService] getPlacePredictions response', {
                    status: data?.status,
                    predictionsCount: Array.isArray(data?.predictions) ? data.predictions.length : undefined,
                    errorMessage: data?.error_message,
                });
            }
            if (data.status && data.status !== 'OK') {
                console.error('Google Places autocomplete failed:', data.status, data.error_message || 'No error_message provided.');
                return [];
            }
            if (!data.predictions) {
                if (data.error_message) {
                    console.error('Google Places autocomplete returned no predictions:', data.error_message);
                }
                return [];
            }
            const predictions = data.predictions.map((pred) => ({
                placeId: pred.place_id,
                mainText: pred.structured_formatting?.main_text || pred.description,
                secondaryText: pred.structured_formatting?.secondary_text,
                fullText: pred.description,
            }));
            // Cache for TTL
            this.requestCache.set(cacheKey, predictions);
            setTimeout(() => this.requestCache.delete(cacheKey), this.cacheTTL);
            return predictions;
        }
        catch (error) {
            console.error('Error fetching place predictions:', error);
            return [];
        }
    }
    /**
     * Get detailed place information including address components
     */
    async getPlaceDetails(placeId, config) {
        try {
            const apiKey = config?.integrations?.googlePlaces?.apiKey || config?.apiKey || config?.integrations?.google?.api_key || this.apiKey;
            if (!apiKey) {
                console.error('Google Places API key not configured');
                return null;
            }
            // Use global proxy to avoid CORS issues
            const proxyURL = config?.integrations?.global?.proxyUrl || '';
            if (debug) {
                console.log('[GooglePlacesService] getPlaceDetails start', {
                    placeId,
                    proxyURL,
                });
            }
            const apiUrl = buildUrl({
                baseUrl: 'https://maps.googleapis.com',
                pathSegments: ['maps', 'api', 'place', 'details', 'json'],
                params: {
                    place_id: placeId,
                    key: apiKey,
                    fields: 'address_component,formatted_address',
                    sessiontoken: this.sessionToken
                },
            });
            const proxyOptions = proxyURL ? { url: proxyURL, forceProxy: true, fallbackOnCors: true } : undefined;
            if (debug) {
                console.log('[GooglePlacesService] getPlaceDetails request', {
                    apiUrl,
                    proxyOptions,
                });
            }
            const data = await smartFetch(apiUrl, {
                proxy: proxyOptions,
            });
            if (debug) {
                console.log('[GooglePlacesService] getPlaceDetails response', {
                    status: data?.status,
                    resultKeys: data?.result ? Object.keys(data.result) : undefined,
                    errorMessage: data?.error_message,
                });
            }
            if (data.status && data.status !== 'OK') {
                console.error('Google Places place details failed:', data.status, data.error_message || 'No error_message provided.');
                return null;
            }
            if (!data.result) {
                if (data.error_message) {
                    console.error('Google Places place details returned no result:', data.error_message);
                }
                return null;
            }
            const result = data.result;
            const addressComponents = result.address_components || [];
            // Parse address components
            const parsed = {
                formattedAddress: result.formatted_address || '',
                addressComponents: addressComponents,
            };
            // Extract standard address fields
            for (const component of addressComponents) {
                const types = component.types || [];
                if (types.includes('street_number') || types.includes('route')) {
                    parsed.street1 = (parsed.street1 || '') + (component.long_name || '') + ' ';
                }
                else if (types.includes('locality')) {
                    parsed.city = component.long_name;
                }
                else if (types.includes('administrative_area_level_1')) {
                    parsed.state = component.short_name;
                }
                else if (types.includes('postal_code')) {
                    parsed.zip = component.long_name;
                }
                else if (types.includes('country')) {
                    parsed.country = component.short_name;
                }
            }
            if (parsed.street1) {
                parsed.street1 = parsed.street1.trim();
            }
            return parsed;
        }
        catch (error) {
            console.error('Error fetching place details:', error);
            return null;
        }
    }
    /**
     * Validate that address is in allowed country
     */
    isValidCountry(placeDetails, allowedCountries = ['US']) {
        if (!placeDetails.country)
            return false;
        return allowedCountries.includes(placeDetails.country.toUpperCase());
    }
    /**
     * Clear cached predictions
     */
    clearCache() {
        this.requestCache.clear();
    }
}
/**
 * Factory function to get configured GooglePlacesService instance
 */
export function getGooglePlacesService(config) {
    return new GooglePlacesService(config);
}
